type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

type DefaultProviderRateLimitPolicy = {
  id: string;
  provider: string;
  operation: string;
  scopeId: string;
  scopeType: "plan" | "plan_user" | "plan_app" | "global";
  rpm: number;
  tpm: number | null;
  maxConcurrent: number;
  queueWaitMs: number;
};

type TierName = "free" | "pro" | "scale";

type TierPolicyValues = Record<
  TierName,
  Pick<DefaultProviderRateLimitPolicy, "rpm" | "tpm" | "maxConcurrent" | "queueWaitMs">
>;

const TIER_PROVIDER_RATE_LIMIT_POLICIES: readonly {
  idSuffix: string;
  provider: string;
  operation: string;
  tiers: TierPolicyValues;
}[] = [
  {
    idSuffix: "openai-ai-chat",
    provider: "openai",
    operation: "ai_chat",
    tiers: {
      free: { rpm: 10, tpm: 20_000, maxConcurrent: 2, queueWaitMs: 3_000 },
      pro: { rpm: 120, tpm: 300_000, maxConcurrent: 10, queueWaitMs: 10_000 },
      scale: { rpm: 600, tpm: 1_500_000, maxConcurrent: 50, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "azure-openai-ai-chat",
    provider: "azure_openai",
    operation: "ai_chat",
    tiers: {
      free: { rpm: 10, tpm: 20_000, maxConcurrent: 2, queueWaitMs: 3_000 },
      pro: { rpm: 120, tpm: 300_000, maxConcurrent: 10, queueWaitMs: 10_000 },
      scale: { rpm: 600, tpm: 1_500_000, maxConcurrent: 50, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "openai-ai-embed",
    provider: "openai",
    operation: "ai_embed",
    tiers: {
      free: { rpm: 30, tpm: 100_000, maxConcurrent: 2, queueWaitMs: 3_000 },
      pro: { rpm: 300, tpm: 2_000_000, maxConcurrent: 20, queueWaitMs: 10_000 },
      scale: { rpm: 1_000, tpm: 10_000_000, maxConcurrent: 50, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "azure-openai-ai-embed",
    provider: "azure_openai",
    operation: "ai_embed",
    tiers: {
      free: { rpm: 30, tpm: 100_000, maxConcurrent: 2, queueWaitMs: 3_000 },
      pro: { rpm: 300, tpm: 2_000_000, maxConcurrent: 20, queueWaitMs: 10_000 },
      scale: { rpm: 1_000, tpm: 10_000_000, maxConcurrent: 50, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "openai-ai-image",
    provider: "openai",
    operation: "ai_image",
    tiers: {
      free: { rpm: 2, tpm: null, maxConcurrent: 1, queueWaitMs: 3_000 },
      pro: { rpm: 20, tpm: null, maxConcurrent: 5, queueWaitMs: 10_000 },
      scale: { rpm: 100, tpm: null, maxConcurrent: 20, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "azure-openai-ai-image",
    provider: "azure_openai",
    operation: "ai_image",
    tiers: {
      free: { rpm: 2, tpm: null, maxConcurrent: 1, queueWaitMs: 3_000 },
      pro: { rpm: 20, tpm: null, maxConcurrent: 5, queueWaitMs: 10_000 },
      scale: { rpm: 100, tpm: null, maxConcurrent: 20, queueWaitMs: 30_000 },
    },
  },
  {
    idSuffix: "openai-document",
    provider: "openai",
    operation: "document_openai",
    tiers: {
      free: { rpm: 2, tpm: 50_000, maxConcurrent: 1, queueWaitMs: 0 },
      pro: { rpm: 30, tpm: 500_000, maxConcurrent: 5, queueWaitMs: 0 },
      scale: { rpm: 150, tpm: 2_000_000, maxConcurrent: 15, queueWaitMs: 0 },
    },
  },
  {
    idSuffix: "google-document-gemini",
    provider: "google",
    operation: "document_gemini",
    tiers: {
      free: { rpm: 2, tpm: 50_000, maxConcurrent: 1, queueWaitMs: 0 },
      pro: { rpm: 30, tpm: 500_000, maxConcurrent: 5, queueWaitMs: 0 },
      scale: { rpm: 150, tpm: 2_000_000, maxConcurrent: 15, queueWaitMs: 0 },
    },
  },
  {
    idSuffix: "google-document-ocr",
    provider: "google",
    operation: "document_ocr",
    tiers: {
      free: { rpm: 2, tpm: 50_000, maxConcurrent: 1, queueWaitMs: 0 },
      pro: { rpm: 30, tpm: 500_000, maxConcurrent: 5, queueWaitMs: 0 },
      scale: { rpm: 150, tpm: 2_000_000, maxConcurrent: 15, queueWaitMs: 0 },
    },
  },
];

function planGuardrailFactor(tier: TierName): number {
  if (tier === "free") return 100;
  if (tier === "pro") return 40;
  return 25;
}

function tierPolicyRows(): DefaultProviderRateLimitPolicy[] {
  return TIER_PROVIDER_RATE_LIMIT_POLICIES.flatMap((policy) =>
    (Object.entries(policy.tiers) as Array<[TierName, TierPolicyValues[TierName]]>).flatMap(
      ([tier, values]) => {
        const guardrailFactor = planGuardrailFactor(tier);
        return [
          {
            id: `default-${tier}-${policy.idSuffix}`,
            provider: policy.provider,
            operation: policy.operation,
            scopeType: "plan" as const,
            scopeId: tier,
            rpm: values.rpm * guardrailFactor,
            tpm: values.tpm == null ? null : values.tpm * guardrailFactor,
            maxConcurrent: values.maxConcurrent * guardrailFactor,
            queueWaitMs: 0,
          },
          {
            id: `default-user-${tier}-${policy.idSuffix}`,
            provider: policy.provider,
            operation: policy.operation,
            scopeType: "plan_user" as const,
            scopeId: tier,
            ...values,
          },
          {
            id: `default-app-${tier}-${policy.idSuffix}`,
            provider: policy.provider,
            operation: policy.operation,
            scopeType: "plan_app" as const,
            scopeId: tier,
            ...values,
          },
        ];
      },
    ),
  );
}

const DEFAULT_PROVIDER_RATE_LIMIT_POLICIES: readonly DefaultProviderRateLimitPolicy[] = [
  ...tierPolicyRows(),
  {
    id: "default-global-openai-ai-chat",
    provider: "openai",
    operation: "ai_chat",
    scopeId: "*",
    scopeType: "global",
    rpm: 30_000,
    tpm: 100_000_000,
    maxConcurrent: 3_000,
    queueWaitMs: 0,
  },
  {
    id: "default-global-azure-openai-ai-chat",
    provider: "azure_openai",
    operation: "ai_chat",
    scopeId: "*",
    scopeType: "global",
    rpm: 30_000,
    tpm: 100_000_000,
    maxConcurrent: 3_000,
    queueWaitMs: 0,
  },
  {
    id: "default-global-openai-ai-embed",
    provider: "openai",
    operation: "ai_embed",
    scopeId: "*",
    scopeType: "global",
    rpm: 50_000,
    tpm: 200_000_000,
    maxConcurrent: 3_000,
    queueWaitMs: 0,
  },
  {
    id: "default-global-azure-openai-ai-embed",
    provider: "azure_openai",
    operation: "ai_embed",
    scopeId: "*",
    scopeType: "global",
    rpm: 50_000,
    tpm: 200_000_000,
    maxConcurrent: 3_000,
    queueWaitMs: 0,
  },
];

function providerRateLimitPolicyValuesSql(): string {
  return DEFAULT_PROVIDER_RATE_LIMIT_POLICIES.map((policy) => {
    const tpm = policy.tpm == null ? "NULL" : String(policy.tpm);
    const reason =
      policy.scopeType === "plan"
        ? "Default plan guardrail provider admission policy"
        : `Default ${policy.scopeType} provider admission policy`;
    return `('${policy.id}', '${policy.provider}', '${policy.operation}', '*', '${policy.scopeType}', '${policy.scopeId}', ${policy.rpm}, NULL, ${tpm}, ${policy.maxConcurrent}, ${policy.queueWaitMs}, TRUE, -100, '${reason}')`;
  }).join(",\n            ");
}

function defaultOpenAiRouteSeedSql(): string[] {
  return [
    `
        INSERT INTO provider_credentials (
            id, provider, display_name, secret_ref, key_fingerprint, status, scopes_json, created_by
        )
        VALUES (
            'default-openai-env', 'openai', 'Default OpenAI environment key',
            'env:PLATFORM_OPENAI_KEY', NULL, 'active', '["ai_chat","ai_embed","ai_image"]', 'system:bootstrap'
        )
        ON CONFLICT (id) DO NOTHING
    `,
    `
        INSERT INTO provider_deployments (
            id, provider, logical_model, operation, credential_id, priority, weight, status,
            fallback_allowed, notes, created_by
        )
        VALUES
            ('default-openai-ai-chat', 'openai', '*', 'ai_chat', 'default-openai-env', -100, 100, 'active', FALSE, 'Default OpenAI env route for chat models', 'system:bootstrap'),
            ('default-openai-ai-embed', 'openai', '*', 'ai_embed', 'default-openai-env', -100, 100, 'active', FALSE, 'Default OpenAI env route for embedding models', 'system:bootstrap'),
            ('default-openai-ai-image', 'openai', '*', 'ai_image', 'default-openai-env', -100, 100, 'active', FALSE, 'Default OpenAI env route for image models', 'system:bootstrap')
        ON CONFLICT (id) DO NOTHING
    `,
  ];
}

export async function ensureProviderRateLimitAdmissionSchema(params: { rawSql: RawSql }): Promise<void> {
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_credentials (
            id TEXT PRIMARY KEY,
            provider TEXT NOT NULL,
            display_name TEXT NOT NULL,
            secret_ref TEXT NOT NULL,
            key_fingerprint TEXT,
            status TEXT NOT NULL DEFAULT 'active',
            scopes_json TEXT NOT NULL DEFAULT '[]',
            created_by TEXT NOT NULL,
            rotated_by TEXT,
            rotated_at TIMESTAMP,
            expires_at TIMESTAMP,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_deployments (
            id TEXT PRIMARY KEY,
            provider TEXT NOT NULL,
            logical_model TEXT NOT NULL,
            operation TEXT NOT NULL,
            credential_id TEXT NOT NULL,
            endpoint TEXT,
            azure_resource_name TEXT,
            azure_deployment_name TEXT,
            azure_api_version TEXT,
            azure_region TEXT,
            azure_subscription_label TEXT,
            azure_deployment_type TEXT,
            model_version TEXT,
            assigned_tpm BIGINT,
            assigned_rpm INTEGER,
            priority INTEGER NOT NULL DEFAULT 0,
            weight INTEGER NOT NULL DEFAULT 100,
            status TEXT NOT NULL DEFAULT 'active',
            fallback_allowed BOOLEAN NOT NULL DEFAULT false,
            notes TEXT,
            created_by TEXT NOT NULL,
            updated_by TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_provider_deployments_route ON provider_deployments (operation, logical_model, provider, status, priority DESC)`,
  );
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_routing_policies (
            id TEXT PRIMARY KEY,
            operation TEXT NOT NULL,
            logical_model_pattern TEXT NOT NULL DEFAULT '*',
            plan_scope TEXT NOT NULL DEFAULT '*',
            preferred_provider TEXT NOT NULL,
            fallback_order_json TEXT NOT NULL DEFAULT '[]',
            require_data_residency BOOLEAN NOT NULL DEFAULT false,
            enabled BOOLEAN NOT NULL DEFAULT true,
            priority INTEGER NOT NULL DEFAULT 0,
            reason TEXT,
            updated_by TEXT NOT NULL,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_provider_routing_policies_lookup ON provider_routing_policies (operation, enabled, priority DESC)`,
  );
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_rate_limit_policies (
            id TEXT PRIMARY KEY,
            provider TEXT NOT NULL,
            operation TEXT NOT NULL,
            model_pattern TEXT NOT NULL DEFAULT '*',
            scope_type TEXT NOT NULL,
            scope_id TEXT NOT NULL DEFAULT '*',
            rpm INTEGER,
            rps INTEGER,
            tpm BIGINT,
            max_concurrent INTEGER,
            queue_wait_ms INTEGER NOT NULL DEFAULT 0,
            enabled BOOLEAN NOT NULL DEFAULT true,
            priority INTEGER NOT NULL DEFAULT 0,
            effective_from TIMESTAMP,
            expires_at TIMESTAMP,
            reason TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_provider_rate_limit_policies_lookup ON provider_rate_limit_policies (provider, operation, enabled, priority DESC)`,
  );
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_admin_audit_events (
            id SERIAL PRIMARY KEY,
            actor_user_id TEXT NOT NULL,
            action TEXT NOT NULL,
            provider TEXT,
            credential_id TEXT,
            deployment_id TEXT,
            reason TEXT,
            request_id TEXT,
            metadata_json TEXT NOT NULL DEFAULT '{}',
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(`ALTER TABLE provider_admin_audit_events ADD COLUMN IF NOT EXISTS request_id TEXT`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_provider_admin_audit_events_created ON provider_admin_audit_events (created_at DESC)`,
  );
  await params.rawSql(`CREATE TABLE IF NOT EXISTS provider_admission_metrics (
            id SERIAL PRIMARY KEY,
            bucket_start TIMESTAMP NOT NULL,
            provider TEXT NOT NULL,
            operation TEXT NOT NULL,
            model TEXT NOT NULL,
            plan TEXT NOT NULL,
            app_id TEXT NOT NULL,
            provider_route_id TEXT NOT NULL DEFAULT '',
            deployment_id TEXT NOT NULL DEFAULT '',
            event_type TEXT NOT NULL,
            reason_code TEXT NOT NULL DEFAULT '',
            count BIGINT NOT NULL DEFAULT 0,
            retry_after_ms_sum BIGINT NOT NULL DEFAULT 0,
            estimated_tokens_sum BIGINT NOT NULL DEFAULT 0,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(
    `ALTER TABLE provider_deployments ALTER COLUMN assigned_tpm TYPE BIGINT USING assigned_tpm::bigint`,
  );
  await params.rawSql(
    `ALTER TABLE provider_rate_limit_policies ALTER COLUMN tpm TYPE BIGINT USING tpm::bigint`,
  );
  await params.rawSql(
    `ALTER TABLE provider_admission_metrics ALTER COLUMN count TYPE BIGINT USING count::bigint`,
  );
  await params.rawSql(
    `ALTER TABLE provider_admission_metrics ALTER COLUMN retry_after_ms_sum TYPE BIGINT USING retry_after_ms_sum::bigint`,
  );
  await params.rawSql(
    `ALTER TABLE provider_admission_metrics ALTER COLUMN estimated_tokens_sum TYPE BIGINT USING estimated_tokens_sum::bigint`,
  );
  await params.rawSql(
    `CREATE UNIQUE INDEX IF NOT EXISTS idx_provider_admission_metrics_bucket ON provider_admission_metrics (bucket_start, provider, operation, model, plan, app_id, provider_route_id, deployment_id, event_type, reason_code)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_provider_admission_metrics_app_recent ON provider_admission_metrics (app_id, bucket_start DESC)`,
  );
  await params.rawSql(`
        INSERT INTO provider_rate_limit_policies (
            id, provider, operation, model_pattern, scope_type, scope_id,
            rpm, rps, tpm, max_concurrent, queue_wait_ms, enabled, priority, reason
        )
        VALUES
            ${providerRateLimitPolicyValuesSql()}
        ON CONFLICT (id) DO UPDATE SET
            provider = EXCLUDED.provider,
            operation = EXCLUDED.operation,
            model_pattern = EXCLUDED.model_pattern,
            scope_type = EXCLUDED.scope_type,
            scope_id = EXCLUDED.scope_id,
            rpm = EXCLUDED.rpm,
            rps = EXCLUDED.rps,
            tpm = EXCLUDED.tpm,
            max_concurrent = EXCLUDED.max_concurrent,
            queue_wait_ms = EXCLUDED.queue_wait_ms,
            enabled = EXCLUDED.enabled,
            priority = EXCLUDED.priority,
            reason = EXCLUDED.reason,
            updated_at = NOW()
        WHERE provider_rate_limit_policies.priority = -100
          AND provider_rate_limit_policies.reason LIKE 'Default % provider admission policy'
    `);
  for (const sql of defaultOpenAiRouteSeedSql()) await params.rawSql(sql);
}
